import React from 'react';
import { Box, Option, SelectDropdown, Text } from '@common-fe/common-fe';

import { ALL_OPTION } from '@/common/constants';
import { OptionKey } from '@/common/types';
import { IAuthorizedUserDtoStatusEnum } from '@/dtos';

import {
  AuthorizedUser,
  CreateAuthorizedUserParams,
  UpdateAuthorizedUserParams,
} from '../authorizedUsers.types';

import AuthorizedUsersHeader from './AuthorizedUsersHeader';
import AuthorizedUsersList from './AuthorizedUsersList';
import AuthorizedUsersModal from './AuthorizedUsersModal';
import DeactivateAuthorizedUserModal from './DeactivateAuthorizedUserModal';
import ReactivateAuthorizedUserModal from './ReactivateAuthorizedUserModal';

const STATUS_OPTIONS: Option[] = [
  ALL_OPTION,
  { key: IAuthorizedUserDtoStatusEnum.ACTIVE, value: IAuthorizedUserDtoStatusEnum.ACTIVE, title: 'Active' },
  { key: IAuthorizedUserDtoStatusEnum.INACTIVE, value: IAuthorizedUserDtoStatusEnum.INACTIVE, title: 'Inactive' },
];

const STATUS_FILTER_INPUT_STYLE = { minWidth: '180px', maxWidth: '180px' };

export interface AuthorizedUsersProps {
  authorizedUsers: AuthorizedUser[];
  filteredUsers: AuthorizedUser[];
  filterStatuses: OptionKey[];
  isLoading: boolean;
  isModalOpen: boolean;
  editingUser: AuthorizedUser | null;
  deactivatingUser: AuthorizedUser | null;
  reactivatingUser: AuthorizedUser | null;
  isSubmitting: boolean;
  isDeactivating: boolean;
  isReactivating: boolean;
  onAddNew: () => void;
  onEdit: (user: AuthorizedUser) => void;
  onDeactivate: (user: AuthorizedUser) => void;
  onReactivate: (user: AuthorizedUser) => void;
  onCreate: (params: CreateAuthorizedUserParams) => void;
  onUpdate: (params: UpdateAuthorizedUserParams) => void;
  onConfirmDeactivate: () => void;
  onConfirmReactivate: () => void;
  onCloseModal: () => void;
  onCloseDeactivateModal: () => void;
  onCloseReactivateModal: () => void;
  onFilterChange: (statuses: OptionKey[]) => void;
}

const AuthorizedUsers: React.FC<AuthorizedUsersProps> = ({
  authorizedUsers,
  filteredUsers,
  filterStatuses,
  isLoading,
  isModalOpen,
  editingUser,
  deactivatingUser,
  reactivatingUser,
  isSubmitting,
  isDeactivating,
  isReactivating,
  onAddNew,
  onEdit,
  onDeactivate,
  onReactivate,
  onCreate,
  onUpdate,
  onConfirmDeactivate,
  onConfirmReactivate,
  onCloseModal,
  onCloseDeactivateModal,
  onCloseReactivateModal,
  onFilterChange,
}) => {
  const deactivatingUserFullName = deactivatingUser
    ? `${deactivatingUser.firstName} ${deactivatingUser.lastName}`
    : '';

  const reactivatingUserFullName = reactivatingUser
    ? `${reactivatingUser.firstName} ${reactivatingUser.lastName}`
    : '';

  return (
    <Box data-testid="authorized-users">
      <AuthorizedUsersHeader onAddNew={onAddNew} />

      <Box
        round="container1Round"
        background="module"
        pad="spacing24"
        margin={{ top: 'spacing16' }}
      >
        <Box direction="row" align="center" justify="between">
          <Text size="14px" weight="bold" color="textBody" data-testid="authorized-users_count">
            Authorized users: {authorizedUsers.length}
          </Text>
          <SelectDropdown
            id="authorized-users-status"
            testId="authorized-users_status-filter"
            ellipsisMode
            activeTitle
            allMode
            name="Status"
            prefix="Status"
            options={STATUS_OPTIONS}
            values={filterStatuses}
            onChangeValues={onFilterChange}
            inputStyle={STATUS_FILTER_INPUT_STYLE}
          />
        </Box>

        <AuthorizedUsersList
          authorizedUsers={filteredUsers}
          isLoading={isLoading}
          onEdit={onEdit}
          onDeactivate={onDeactivate}
          onReactivate={onReactivate}
        />
      </Box>

      {isModalOpen && (
        <AuthorizedUsersModal
          editingUser={editingUser}
          isSubmitting={isSubmitting}
          onCreate={onCreate}
          onUpdate={onUpdate}
          onClose={onCloseModal}
        />
      )}

      {deactivatingUser && (
        <DeactivateAuthorizedUserModal
          userFullName={deactivatingUserFullName}
          isDeactivating={isDeactivating}
          onConfirm={onConfirmDeactivate}
          onClose={onCloseDeactivateModal}
        />
      )}

      {reactivatingUser && (
        <ReactivateAuthorizedUserModal
          userFullName={reactivatingUserFullName}
          isReactivating={isReactivating}
          onConfirm={onConfirmReactivate}
          onClose={onCloseReactivateModal}
        />
      )}
    </Box>
  );
};

export default AuthorizedUsers;
